import { CheckCircle, CreditCard, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";

const QuoteSignAndPay = ({
  quote,
  isSigned,
  isPaid,
  isDepositPaid,
  isFullPayment,
  paymentAmount,
  paymentLabel,
  paymentBase,
  paymentFee,
  depositAmount,
  depositBase,
  balanceAmount,
  balanceBase,
  changeOrders,
  changeOrdersTotal,
  terms,
  showTerms,
  setShowTerms,
  signatureName,
  setSignatureName,
  acceptTerms,
  setAcceptTerms,
  signing,
  processingPayment,
  showCashCheckInfo,
  setShowCashCheckInfo,
  handleSign,
  handlePayment,
  handleCashCheckPayment,
}) => (
  <>
    {/* Terms & Conditions */}
    <div className="p-8 border-b border-zinc-200 bg-zinc-50">
      <button
        onClick={() => setShowTerms(!showTerms)}
        className="flex items-center justify-between w-full p-4 bg-white rounded-lg border border-zinc-200 hover:bg-zinc-100 transition-colors"
      >
        <span className="font-medium text-zinc-700">View Terms & Conditions</span>
        {showTerms
          ? <ChevronUp className="w-5 h-5 text-zinc-500" />
          : <ChevronDown className="w-5 h-5 text-zinc-500" />}
      </button>
      {showTerms && (
        <div className="mt-2 p-4 bg-white rounded-lg border border-zinc-200 max-h-96 overflow-y-auto">
          <pre className="text-xs text-zinc-600 whitespace-pre-wrap font-sans">{terms}</pre>
        </div>
      )}
    </div>

    {/* Signature + Payment Section */}
    {!isPaid && (
      <div className="p-8 bg-white">
        <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-[#00341d] mb-6">
          Accept & Sign
        </h3>

        {isSigned ? (
          <div className="space-y-6">
            {/* Signed confirmation */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <span className="font-bold text-green-800">Quote Signed</span>
              </div>
              <p className="text-green-700">
                Signed by <strong>{quote.signature_name}</strong> on{" "}
                {new Date(quote.signature_date).toLocaleDateString("en-US", {
                  year: "numeric", month: "long", day: "numeric",
                  hour: "2-digit", minute: "2-digit",
                })}
              </p>
            </div>

            {/* Payment */}
            {quote.payment_method === "cash_check" ? (
              <CashCheckConfirmation
                paymentBase={paymentBase}
                handlePayment={handlePayment}
                processingPayment={processingPayment}
              />
            ) : (
              <CardPaymentSection
                quote={quote}
                isPaid={isPaid}
                isDepositPaid={isDepositPaid}
                isFullPayment={isFullPayment}
                paymentAmount={paymentAmount}
                paymentLabel={paymentLabel}
                paymentBase={paymentBase}
                paymentFee={paymentFee}
                depositAmount={depositAmount}
                depositBase={depositBase}
                balanceAmount={balanceAmount}
                balanceBase={balanceBase}
                changeOrders={changeOrders}
                changeOrdersTotal={changeOrdersTotal}
                processingPayment={processingPayment}
                showCashCheckInfo={showCashCheckInfo}
                setShowCashCheckInfo={setShowCashCheckInfo}
                handlePayment={handlePayment}
                handleCashCheckPayment={handleCashCheckPayment}
              />
            )}
          </div>
        ) : (
          <SignatureForm
            signatureName={signatureName}
            setSignatureName={setSignatureName}
            acceptTerms={acceptTerms}
            setAcceptTerms={setAcceptTerms}
            signing={signing}
            handleSign={handleSign}
          />
        )}
      </div>
    )}

    {/* Paid Confirmation */}
    {isPaid && (
      <div className="p-8 bg-green-50">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h3 className="font-heading text-2xl font-bold text-green-800 uppercase mb-2">
            Payment Complete
          </h3>
          <p className="text-green-700">
            Thank you for your payment. We will be in touch soon to schedule your service.
          </p>
        </div>
      </div>
    )}
  </>
);

const CashCheckConfirmation = ({ paymentBase, handlePayment, processingPayment }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 sm:p-8 text-center">
    <CheckCircle className="w-12 h-12 text-amber-600 mx-auto mb-4" />
    <h4 className="text-amber-900 text-xl font-bold mb-2">Cash/Check Payment Selected</h4>
    <p className="text-amber-700 mb-4">
      Amount Due: <strong>${paymentBase?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
    </p>
    <p className="text-amber-600 text-xs mb-4">No 3% card processing fee — pay the quoted price as-is.</p>
    <div className="bg-white border border-amber-200 rounded-lg p-4 mb-4 text-left max-w-sm mx-auto">
      <p className="text-amber-800 text-sm mb-2">Please make checks payable to:</p>
      <p className="text-amber-900 font-bold">Land ClearCo, LLC</p>
      <p className="text-amber-700 text-sm">3356 Alleghany Dr.</p>
      <p className="text-amber-700 text-sm">Raleigh, NC 27609</p>
    </div>
    <div className="mt-6 pt-4 border-t border-amber-200">
      <p className="text-amber-600 text-sm mb-3">Changed your mind?</p>
      <Button
        onClick={handlePayment}
        disabled={processingPayment}
        variant="outline"
        className="border-amber-400 text-amber-700 hover:bg-amber-100"
      >
        {processingPayment ? "Processing..." : "Pay by Card Instead"}
      </Button>
    </div>
  </div>
);

const CardPaymentSection = ({
  quote,
  isPaid,
  isDepositPaid,
  isFullPayment,
  paymentAmount,
  paymentLabel,
  paymentBase,
  paymentFee,
  depositAmount: _depositAmount,
  depositBase,
  balanceAmount: _balanceAmount,
  balanceBase,
  changeOrders,
  changeOrdersTotal,
  processingPayment,
  showCashCheckInfo,
  setShowCashCheckInfo,
  handlePayment,
  handleCashCheckPayment,
}) => (
  <>
    {isDepositPaid && !isFullPayment && (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-800 font-medium">
            Deposit Paid: ${(quote.deposit_paid_amount ?? depositBase)?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </span>
        </div>
        {quote.deposit_paid_date && (
          <p className="text-green-600 text-sm mt-1 ml-7">
            Paid on {new Date(quote.deposit_paid_date).toLocaleDateString()}
          </p>
        )}
      </div>
    )}

    {isPaid ? (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
        <h4 className="text-green-800 text-xl font-bold mb-2">Payment Complete</h4>
        <p className="text-green-600">Thank you for your payment!</p>
      </div>
    ) : (
      <div className="bg-[#00341d] rounded-lg p-6 sm:p-8 text-center">
        <CreditCard className="w-12 h-12 text-white mx-auto mb-4" />
        <h4 className="text-white text-xl font-bold mb-4">
          {isDepositPaid ? "Pay Remaining Balance" : "Complete Your Payment"}
        </h4>

        {/* Clear breakdown: base + 3% fee = total — so the customer sees exactly
            what they'll be charged on the card and why */}
        <div className="bg-white/10 rounded-lg p-4 mb-6 text-left max-w-sm mx-auto" data-testid="card-payment-breakdown">
          {isDepositPaid && changeOrders?.length > 0 && (
            <div className="mb-3 pb-2 border-b border-white/20" data-testid="balance-change-orders">
              <div className="text-white/70 text-xs uppercase tracking-wide mb-1">Includes change orders</div>
              {changeOrders.map((co) => (
                <div key={co.id} className="flex justify-between text-white/80 text-sm" data-testid={`balance-co-${co.id}`}>
                  <span>{co.change_order_number}</span>
                  <span>${Number(co.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-between text-white/80 mb-1.5">
            <span>{paymentLabel}</span>
            <span>${paymentBase?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between text-white/60 text-sm mb-2" data-testid="card-fee-line">
            <span>3% Card processing fee</span>
            <span>+ ${paymentFee?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="border-t border-white/20 mt-2 pt-2 flex justify-between text-white font-bold">
            <span>Total to charge today</span>
            <span>${paymentAmount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          </div>
          {!isFullPayment && !isDepositPaid && (
            <div className="border-t border-white/10 mt-3 pt-2 flex justify-between text-white/60 text-xs">
              <span>Remaining balance after deposit</span>
              <span>${balanceBase?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
          )}
        </div>

        <p className="text-white/60 text-sm mb-6">
          Accepts Apple Pay, Google Pay, and all major credit cards
        </p>
        <Button
          onClick={() => handlePayment()}
          disabled={processingPayment}
          className="bg-[#d97706] hover:bg-[#b45309] text-white font-bold uppercase tracking-wide px-8 h-12 text-lg shadow-lg hover:shadow-xl transition-all duration-200"
          data-testid="pay-now-button"
        >
          {processingPayment ? (
            <span className="flex items-center gap-2">
              <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Processing...
            </span>
          ) : (
            <>Pay ${paymentAmount?.toLocaleString(undefined, { minimumFractionDigits: 2 })} Now</>
          )}
        </Button>

        {!isDepositPaid && (
          <div className="mt-6 pt-4 border-t border-white/20">
            {!showCashCheckInfo ? (
              <button
                onClick={() => setShowCashCheckInfo(true)}
                className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg text-base font-medium transition-colors w-full"
                data-testid="cash-check-button"
              >
                Pay by Cash or Check (no 3% fee)
              </button>
            ) : (
              <div className="bg-white/10 rounded-lg p-4 text-left">
                <h5 className="text-white font-semibold mb-3 text-center">Pay by Cash or Check</h5>
                <p className="text-white/80 text-sm text-center mb-3">
                  Amount due: <strong>${paymentBase?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                </p>
                <div className="bg-white/10 rounded p-4 mb-4">
                  <p className="text-white/90 text-sm mb-2">Please make checks payable to:</p>
                  <p className="text-white font-bold">Land ClearCo, LLC</p>
                  <p className="text-white/80 text-sm">3356 Alleghany Dr.</p>
                  <p className="text-white/80 text-sm">Raleigh, NC 27609</p>
                </div>
                <p className="text-white/60 text-xs text-center mb-4">
                  No processing fee for cash/check payments
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowCashCheckInfo(false)}
                    className="flex-1 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded text-sm transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleCashCheckPayment}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
                    data-testid="confirm-cash-check-button"
                  >
                    Confirm Cash/Check
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )}
  </>
);

const SignatureForm = ({
  signatureName,
  setSignatureName,
  acceptTerms,
  setAcceptTerms,
  signing,
  handleSign,
}) => (
  <div className="space-y-6">
    <div className="border border-zinc-200 rounded-lg p-4 sm:p-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="signature-name" className="text-sm font-medium text-zinc-700">
            Full Legal Name
          </Label>
          <Input
            id="signature-name"
            value={signatureName}
            onChange={(e) => setSignatureName(e.target.value)}
            placeholder="Type your full name as signature"
            className="mt-2 h-12 text-base sm:text-lg"
            data-testid="signature-name-input"
          />
        </div>
        <div className="flex items-start gap-3">
          <Checkbox
            id="accept-terms"
            checked={acceptTerms}
            onCheckedChange={setAcceptTerms}
            className="mt-1 h-5 w-5 flex-shrink-0"
            data-testid="accept-terms-checkbox"
          />
          <Label htmlFor="accept-terms" className="text-sm text-zinc-600 leading-relaxed cursor-pointer">
            I have read and agree to the Terms and Conditions. I understand that by typing my name above,
            I am providing my electronic signature and accepting this quote.
          </Label>
        </div>
        <Button
          onClick={handleSign}
          disabled={signing || !signatureName || !acceptTerms}
          className="w-full bg-[#00341d] hover:bg-[#002816] text-white font-bold uppercase tracking-wide h-12 sm:h-14 text-base sm:text-lg shadow-md transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0"
          data-testid="sign-quote-button"
        >
          {signing ? (
            <span className="flex items-center gap-2">
              <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Signing...
            </span>
          ) : "Sign Quote"}
        </Button>
      </div>
    </div>
  </div>
);

export default QuoteSignAndPay;
